import { formatISO, parseISO } from "date-fns";
import {
    TransactionColumn,
    TransactionView,
} from "@common/types/views/transactionViews";
import { NamedView } from "@common/types/views/views";
import { TransactionsFilters } from "@components/transactions/filters/lib";
import { createUnreconciledChecksView } from "@components/transactions/views/UnreconciledChecksView";

interface CreateReconciliationClearViewOptions {
    entityId: number;
    financialAccountId: number;
    statementsClosingDate: string;
    hasAnyClass: boolean;
    isGlFirst: boolean;
    excludedTransactionIds: number[];
}

type TransactionViewColumns = NamedView<TransactionView>["columns"];

function withCashLedgerColumn(
    columns: TransactionViewColumns,
): TransactionViewColumns {
    if (columns.some((column) => column.id === TransactionColumn.LEDGER)) {
        return columns;
    }

    const ledgerColumn = { id: TransactionColumn.LEDGER };
    const accountIndex = columns.findIndex(
        (column) => column.id === TransactionColumn.ACCOUNT,
    );
    if (accountIndex === -1) {
        return [...columns, ledgerColumn];
    }

    return [
        ...columns.slice(0, accountIndex),
        ledgerColumn,
        ...columns.slice(accountIndex),
    ];
}

export function createReconciliationClearView({
    entityId,
    financialAccountId,
    statementsClosingDate,
    hasAnyClass,
    isGlFirst,
    excludedTransactionIds,
}: CreateReconciliationClearViewOptions): NamedView<TransactionView> {
    const base = createUnreconciledChecksView({
        entityIds: [entityId],
        hasAnyClass,
        isGlFirst,
    });

    return {
        ...base,
        options: {
            ...base.options,
            bulkActions: false,
            selection: false,
        },
        columns: [
            ...withCashLedgerColumn(base.columns).map((column) =>
                column.id === TransactionColumn.AMOUNT
                    ? { ...column, id: TransactionColumn.ORIGINAL_AMOUNT }
                    : column,
            ),
            {
                id: TransactionColumn.CTA,
                options: {
                    pin: "right",
                    label: "Match",
                    onClick: () => undefined,
                },
            },
        ],
        filters: {
            ...base.filters,
            accountIds: [financialAccountId],
            excludeSplits: true,
            includeBeforeBookkeepingStart: true,
            end: parseISO(statementsClosingDate),
            ...(excludedTransactionIds.length > 0
                ? { excludedTransactionIds }
                : {}),
        },
    };
}

interface ClampClearStepDatesParams {
    filters: TransactionsFilters;
    statementsClosingDate: string;
}

function toLocalDateOnly(value: Date): string {
    return formatISO(value, { representation: "date" });
}

function dateNotAfter(value: Date, bound: string): Date {
    if (toLocalDateOnly(value) < bound) {
        return value;
    }
    return parseISO(bound);
}

export function clampClearStepDates({
    filters,
    statementsClosingDate,
}: ClampClearStepDatesParams): TransactionsFilters {
    return {
        ...filters,
        ...(filters.end
            ? { end: dateNotAfter(filters.end, statementsClosingDate) }
            : {}),
    };
}
